// autopilot-doctor-no-mission.ts
//
// Req 3 (phase 05, D9): `autopilot doctor` for a workspace with ZERO
// missions yet — split out of autopilot-doctor-command.ts to keep that
// file's action body under the 200-line budget. Before this phase,
// `resolveMission` threw immediately (Key Insight #2), so a fresh workspace
// could never run `doctor` at all; this module is what makes the
// environment-only path work.
import {
  environmentChecksBlockRun,
  runEnvironmentChecks,
} from "../lib/autopilot/autopilot-environment-checks.js";
import { MissionResolutionError } from "../lib/autopilot/mission-resolver.js";
import { writeMachineReadableJson } from "../lib/machine-readable-json-output-contract.js";
import { log } from "../lib/terminal-logger.js";
import {
  buildDoctorEnvironmentDeps,
  parseGlobalConfigReadOnly,
} from "./autopilot-doctor-environment-deps.js";

/** True only for the specific "0 missions, no --mission" shape (Req 3) — any OTHER resolution error (bad id, ambiguous) still surfaces normally. */
export function isNoMissionsYetError(err: unknown, explicitMissionId: string | undefined): boolean {
  return (
    explicitMissionId === undefined &&
    err instanceof MissionResolutionError &&
    err.candidates.length === 0
  );
}

/** Environment-only doctor: no controlRoot to snapshot config into, so config is parsed read-only here. Sets `process.exitCode = 1` on any `fail` (Req 4). */
export async function runEnvironmentOnlyDoctor(
  workspaceRoot: string,
  globalConfigPath: string,
  json: boolean,
): Promise<void> {
  const configResult = await parseGlobalConfigReadOnly(globalConfigPath);
  const checks = configResult.ok
    ? await runEnvironmentChecks(
        await buildDoctorEnvironmentDeps({
          workspaceRoot,
          config: configResult.config,
          unusedConfigKeyWarnings: configResult.unusedKeyWarnings,
        }),
      )
    : [
        {
          name: "config",
          status: "fail" as const,
          detail: `config_invalid — ${configResult.message}`,
        },
      ];
  const blocksRun = environmentChecksBlockRun(checks);

  if (json) {
    writeMachineReadableJson({
      command: "autopilot doctor",
      checks,
      blocks_run: blocksRun,
      summary: {
        total: checks.length,
        passed: checks.filter((c) => c.status === "ok").length,
        issues: checks.filter((c) => c.status !== "ok").length,
      },
    });
    if (blocksRun) process.exitCode = 1;
    return;
  }
  for (const c of checks) log.info(`[${c.status}] ${c.name}: ${c.detail}`);
  log.info(
    "Chưa có mission nào — chỉ kiểm nhóm môi trường (chạy 'avatar autopilot init' để tạo mission).",
  );
  if (blocksRun) {
    log.error("Doctor chặn 'run' — xem check ở trên.");
    process.exitCode = 1;
  }
}
